// File: components/content/Breadcrumb.js

import React from 'react';
import { ICONS } from '../../utils/constants';

/**
 * Breadcrumb Component
 * 
 * Displays the current location in the regulation hierarchy
 * Mobile-optimized with truncation for long paths
 * 
 * Props:
 * - path: Array of breadcrumb items [{label, url}]
 * - onNavigate: Function called when clicking breadcrumb (optional)
 * - maxItems: Maximum items to show before truncation (default: 4)
 * - separator: Custom separator (default: '›')
 */
const Breadcrumb = ({ 
    path = [], 
    onNavigate = null,
    maxItems = 4,
    separator = ICONS.ARROW_RIGHT || '›'
}) => {
    // Don't render if no path provided
    if (!path || path.length === 0) {
        return null;
    }

    // Handle breadcrumb click
    const handleClick = (item, index, e) => {
        e.preventDefault();
        
        if (onNavigate && typeof onNavigate === 'function') {
            onNavigate(item, index);
        } else if (item.url) {
            // Fallback to default navigation
            window.location.href = item.url;
        }
    };

    // Truncate path if too long (mobile optimization)
    const shouldTruncate = path.length > maxItems;
    let displayPath = [...path];
    
    if (shouldTruncate) {
        // Always show first and last, truncate middle
        const firstItem = displayPath[0];
        const lastItems = displayPath.slice(-2); // Last 2 items
        displayPath = [firstItem, { label: '...', isEllipsis: true }, ...lastItems];
    }

    return (
        <nav className="breadcrumb" aria-label="Breadcrumb navigation">
            <ol className="breadcrumb-list">
                {displayPath.map((item, index) => {
                    const isLast = index === displayPath.length - 1;
                    const isEllipsis = item.isEllipsis;
                    
                    return (
                        <li 
                            key={`${item.label}-${index}`} 
                            className={`breadcrumb-item ${isLast ? 'active' : ''}`}
                        >
                            {isEllipsis ? (
                                <span className="breadcrumb-ellipsis" aria-hidden="true">
                                    {item.label}
                                </span>
                            ) : isLast ? (
                                <span 
                                    className="breadcrumb-current"
                                    aria-current="page"
                                >
                                    {item.label}
                                </span>
                            ) : (
                                <a
                                    href={item.url || '#'}
                                    className="breadcrumb-link"
                                    onClick={(e) => handleClick(item, index, e)}
                                >
                                    {item.label}
                                </a>
                            )}
                            
                            {!isLast && (
                                <span 
                                    className="breadcrumb-separator" 
                                    aria-hidden="true"
                                >
                                    {separator}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Breadcrumb;
